import { isEqual } from "lodash";

import { ColumnFilter } from "../../display-table/filterTypes";
import {
  ExploreField,
  ExploreFieldId,
  ExploreFieldType,
  ExploreMeasureOrderFilter,
  ExploreSpec,
  getExploreTimeUnitName,
} from "../../explore/types";
import {
  FilterHqlOptions,
  addFiltersToHqlBuilder,
} from "../../filter/hql/filterHql";
import { addCalcReferencedParamsToHqlBuilder } from "../../hql/calcsHql";
import {
  HqlAggregationBuilder,
  HqlNextSpecBuilder,
  HqlNextTemporaryParamMapping,
} from "../../hql/HqlNextSpecBuilder";
import { getDatasetPathForField } from "../../hql/hqlNextUtils";
import { HqlAggregationFunction } from "../../hql/types";
import * as HexSLTypes from "../../semantic-layer/generated/hex_sl_schema";
import { LumpSpecification } from "../../semantic-layer/generated/hex_sl_schema";
import { ExploreCalcsDefinition } from "../exploreCalcsDefinitionTypes";
import { COUNT_STAR_ARG } from "../exploreConstants";
import {
  generateColumnIdForField,
  getExploreFieldName,
} from "../exploreFieldUtils";
import {
  getLumpPredicateString,
  resolveLumpOrderBy,
} from "../exploreLumpUtils";
import { mapExploreFieldsToPivotFieldsObject } from "../explorePivotUtils";

export const HEX_TOTAL_GROUP_PLACEHOLDER = "_HEX_TOTAL_GROUP_PLACEHOLDER";

export function getExploreCellPivotHqlQuery({
  baseDataset,
  calcs,
  colGroupBys = "all",
  filterOptions,
  filters,
  limit,
  rowGroupBys = "all",
  spec,
}: {
  spec: ExploreSpec;
  filterOptions?: FilterHqlOptions;
  filters?: ColumnFilter[];
  limit?: number;
  /**
   * This is whatever should be in the "from" clause - either the semantic dataset name or
   */
  baseDataset: string;
  calcs: ExploreCalcsDefinition | undefined;
  colGroupBys?: number | "all";
  rowGroupBys?: number | "all";
}): {
  query: HexSLTypes.HqlQuery;
  temporaryParamMapping: HqlNextTemporaryParamMapping;
} {
  const builder = new HqlNextSpecBuilder(baseDataset);
  const aggregationBuilder = builder.addAggregations();

  addPivotAggToHqlBuilder({
    exploreSpec: spec,
    from: baseDataset,
    aggregationBuilder,
    filters,
    filterOptions,
    calcs,
    colGroupBys,
    rowGroupBys,
  });

  if (limit != null) {
    aggregationBuilder.limit(limit);
  }

  aggregationBuilder.buildAggregation();
  return builder.build();
}

export interface PivotQueryConfig {
  // number of group-bys to apply per axis, "all" means group-by-all
  rowGroupBys: number | "all";
  colGroupBys: number | "all";
}

export function isRowTotals(c: PivotQueryConfig): boolean {
  return c.rowGroupBys === "all" && c.colGroupBys === 0;
}

export function isColumnTotals(c: PivotQueryConfig): boolean {
  return c.rowGroupBys === 0 && c.colGroupBys === "all";
}

export function isGrandTotal(c: PivotQueryConfig): boolean {
  return c.rowGroupBys === 0 && c.colGroupBys === 0;
}

export function isMainQuery(c: PivotQueryConfig): boolean {
  return c.rowGroupBys === "all" && c.colGroupBys === "all";
}

export function pivotNameSuffix(
  c: PivotQueryConfig | undefined,
  grandLabel: string = "_bothTotals",
): string {
  if (c == null) {
    return "";
  }
  return isMainQuery(c)
    ? ""
    : isRowTotals(c)
      ? "_rowTotals"
      : isColumnTotals(c)
        ? "_colTotals"
        : isGrandTotal(c)
          ? grandLabel
          : `_rowRank${c.rowGroupBys}_colRank${c.colGroupBys}`;
}

/*
 * mark as true all queries required or implied by the display configuration
 * matrix[r][c] is true if the query with R row groupbys and C column groupbys is required
 * by the display configuration
 * see the test suite for visual explanations and snapshot tests
 */
export function pivotQueryMatrixForDisplay(spec: ExploreSpec): boolean[][] {
  const { columns, rows, values } = mapExploreFieldsToPivotFieldsObject(
    spec.fields,
  );

  const rcGroupBys: boolean[][] = Array(rows.length + 1)
    .fill(null)
    .map(() => Array(columns.length + 1).fill(false));

  // main query always required
  rcGroupBys[rows.length]![columns.length] = true;

  // if no values, no (sub)total queries are required
  if (values.length > 0) {
    // row totals
    rcGroupBys[rows.length]![0] ||= spec.rowTotals ?? false;

    // row subtotals
    for (let c = 1; c < columns.length; c++) {
      rcGroupBys[rows.length]![c] ||= spec.rowSubtotals ?? false;
    }

    // column totals
    rcGroupBys[0]![columns.length] ||= spec.columnTotals ?? false;

    // column subtotals
    for (let r = 1; r < rows.length; r++) {
      rcGroupBys[r]![columns.length] ||= spec.columnSubtotals ?? false;
    }
  }

  // now mark all intersections as required, so we don't have empty cells in the pivot table
  // e.g. if row totals and column totals are required, then the intersection of row totals and column totals is required
  for (let r = 0; r <= rows.length; r++) {
    for (let c = 0; c <= columns.length; c++) {
      const sameRowRequired = rcGroupBys[r]!.some((val, idx) => idx > c && val);
      const sameColRequired = rcGroupBys.some((row, idx) => idx > r && row![c]);

      if (sameRowRequired && sameColRequired) {
        rcGroupBys[r]![c] ||= true;
      }
    }
  }
  return rcGroupBys;
}

/*
 * mark as true all queries required just by the sorting configuration
 * matrix[r][c] is true if the query with R row groupbys and C column groupbys is required
 * by the sorting configuration
 * see the test suite for visual explanations and snapshot tests
 */
export function pivotQueryMatrixForSorting(spec: ExploreSpec): boolean[][] {
  const { columns, rows, values } = mapExploreFieldsToPivotFieldsObject(
    spec.fields,
  );
  const rcGroupBys: boolean[][] = Array(rows.length + 1)
    .fill(null)
    .map(() => Array(columns.length + 1).fill(false));

  rows.forEach((row) => {
    if (hasValidMeasureOrder(row, values)) {
      const numFilters = usableMeasureOrderFilters(row, columns).length;
      rows.forEach((_, i) => {
        rcGroupBys[i + 1]![numFilters] ||= true;
      });
    }
  });
  columns.forEach((col) => {
    if (hasValidMeasureOrder(col, values)) {
      const numFilters = usableMeasureOrderFilters(col, rows).length;
      columns.forEach((_, j) => {
        rcGroupBys[numFilters]![j + 1] ||= true;
      });
    }
  });
  return rcGroupBys;
}

/**
 * Helper function to generate all the permutations of pivot queries that need to be run
 * based on the spec.
 */
export function getAllPivotQueryConfigs(spec: ExploreSpec): PivotQueryConfig[] {
  const { columns, rows } = mapExploreFieldsToPivotFieldsObject(spec.fields);

  // figure out which queries are required
  // matrix[r][c] is true if the query with R row groupbys and C column groupbys is required
  const displayMatrix: boolean[][] = pivotQueryMatrixForDisplay(spec);
  const sortingMatrix: boolean[][] = pivotQueryMatrixForSorting(spec);

  const configs: PivotQueryConfig[] = [];
  // iterate in reverse to ensure main query is first
  for (let r = rows.length; r >= 0; r--) {
    for (let c = columns.length; c >= 0; c--) {
      if (displayMatrix[r]![c] || sortingMatrix[r]![c]) {
        configs.push({
          rowGroupBys: r === rows.length ? "all" : r,
          colGroupBys: c === columns.length ? "all" : c,
        });
      }
    }
  }

  return configs;
}

/**
 * Helper function to determine if the hql will change between two different pivot specs
 * This is used to determine if we need to rerun the query and rerender the pivot table when a change is made
 */
export function isPivotHqlSpecsChanged(
  prev: ExploreSpec,
  next: ExploreSpec,
): boolean {
  // We must compare all the queries (main query, row totals, column totals, grand totals) to determine if the hql has changed
  const prevConfigs = getAllPivotQueryConfigs(prev);
  const nextConfigs = getAllPivotQueryConfigs(next);
  if (prevConfigs.length !== nextConfigs.length) {
    return true;
  }
  for (let i = 0; i < prevConfigs.length; i++) {
    const prevQuery = getExploreCellPivotHqlQuery({
      spec: prev,
      baseDataset: "sentinel_dataframe_value",
      calcs: undefined,
      rowGroupBys: prevConfigs[i]!.rowGroupBys,
      colGroupBys: prevConfigs[i]!.colGroupBys,
    });
    const nextQuery = getExploreCellPivotHqlQuery({
      spec: next,
      baseDataset: "sentinel_dataframe_value",
      calcs: undefined,
      rowGroupBys: nextConfigs[i]!.rowGroupBys,
      colGroupBys: nextConfigs[i]!.colGroupBys,
    });
    if (!isEqual(prevQuery, nextQuery)) {
      return true;
    }
  }
  return false;
}

export function addPivotAggToHqlBuilder({
  aggregationBuilder,
  calcs,
  colGroupBys = "all",
  exploreSpec,
  filterOptions,
  filters,
  rowGroupBys = "all",
}: {
  exploreSpec: ExploreSpec;
  from: string;
  aggregationBuilder: HqlAggregationBuilder;
  filterOptions?: FilterHqlOptions;
  filters?: ColumnFilter[];
  calcs: ExploreCalcsDefinition | undefined;
  colGroupBys?: number | "all";
  rowGroupBys?: number | "all";
}): void {
  if (calcs) {
    addCalcReferencedParamsToHqlBuilder({
      builder: aggregationBuilder,
      calcs,
    });
  }

  const {
    columns: columnFields,
    rows: rowFields,
    values: valueFields,
  } = mapExploreFieldsToPivotFieldsObject(exploreSpec.fields);

  const measureAliases: Record<string, string> = {};
  // We sort the value fields because any order of the value fields
  // is semantically equivalent, and we want to avoid unnecessary re-renders
  valueFields.sort((a, b) => a.id.localeCompare(b.id));
  valueFields.forEach((field) => {
    measureAliases[getExploreFieldName(field)] = field.value;
    if (field.fieldType === ExploreFieldType.MEASURE) {
      if (field.value === COUNT_STAR_ARG) {
        // hardcoded count(*) aggregation
        aggregationBuilder.aggregation({
          expr: { fun: "count", args: [] },
          dataset: getDatasetPathForField(field),
          alias: getExploreFieldName(field),
        });
      } else {
        aggregationBuilder.aggregation({
          measure: field.value,
          dataset: getDatasetPathForField(field),
          alias: getExploreFieldName(field),
        });
      }
    } else {
      if (field.aggregation == null) {
        throw new Error("Aggregation is required");
      }
      aggregationBuilder.aggregation({
        expr: {
          fun: field.aggregation.toLowerCase() as Lowercase<HqlAggregationFunction>,
          args: [{ name: field.value }],
        },
        dataset: getDatasetPathForField(field),
        alias: getExploreFieldName(field),
      });
    }
  });

  const groupByFields = [...rowFields, ...columnFields];
  // We sort the groupByfields because any order of the group by fields
  // is semantically equivalent, and we want to avoid unnecessary re-renders
  groupByFields.sort((a, b) => a.id.localeCompare(b.id));
  const groupByConstant = (
    field: ExploreField,
    directionFields: ExploreField[],
    directionGroupBys: number | "all",
  ): boolean => {
    const fieldIndex = directionFields.indexOf(field);
    if (
      fieldIndex === -1 || // field not in direction
      directionGroupBys === "all" // no constant groups in this direction
    ) {
      return false;
    }
    // we group by a constant value for every field after the first N group-bys
    // given fields a, b, c and group-bys = 1
    // we return false for a (fieldIndex = 0) and true for b and c (fieldIndex = 1 and 2)
    return fieldIndex >= directionGroupBys;
  };

  groupByFields.forEach((field) => {
    if (
      groupByConstant(field, rowFields, rowGroupBys) ||
      groupByConstant(field, columnFields, colGroupBys)
    ) {
      // group by a constant value to create a total row/column
      aggregationBuilder.groupby({
        expr: `'${HEX_TOTAL_GROUP_PLACEHOLDER}'`,
        alias: field.truncUnit
          ? getExploreFieldName(field)
          : generateColumnIdForField(field),
        dataset: getDatasetPathForField(field),
      });
    } else {
      if (field.truncUnit) {
        aggregationBuilder.groupby({
          expr: { name: field.value },
          alias: getExploreFieldName(field),
          time_unit: getExploreTimeUnitName(
            field.truncUnit,
            field.truncUnitConfig,
          ),
          dataset: getDatasetPathForField(field),
        });
      } else {
        const alias = generateColumnIdForField(field);
        const resolvedLumpOrderBy = resolveLumpOrderBy(
          field.lump?.orderBy
            ? ExploreFieldId.check(field.lump.orderBy)
            : undefined,
          field.seriesId,
          exploreSpec,
        );
        let lump: LumpSpecification | undefined;
        if (resolvedLumpOrderBy != null && field.lump != null) {
          lump = {
            window_function: field.lump.windowFunction,
            order_by: resolvedLumpOrderBy,
            order_direction: field.lump.orderDirection,
            predicate: getLumpPredicateString(field.lump.predicate),
            other_label: field.lump.otherLabel,
          };
        }
        if (alias === field.value) {
          // if theres no query path for this field, then grouping by dimension
          // keeps the generated SQL more simple.
          aggregationBuilder.groupby({
            dimension: field.value,
            dataset: getDatasetPathForField(field),
            ...(lump == null ? {} : { lump }),
          });
        } else {
          aggregationBuilder.groupby({
            expr: { name: field.value },
            dataset: getDatasetPathForField(field),
            alias: generateColumnIdForField(field),
            ...(lump == null ? {} : { lump }),
          });
        }
      }
    }
  });

  if (filters && filters.length > 0) {
    addFiltersToHqlBuilder({
      builder: aggregationBuilder,
      filters,
      aliasToOriginalColumnName: measureAliases,
      options: filterOptions,
      exploreSpec,
    });
  }
}

/*
 * Returns the filters which will actually be used
 * it's possible for the sort order to point to, say, fields A, B and C
 * but then for the pivot to now contain fields A, C, D, B in order
 * in this case we will only apply the filter for A and C (and for D & B we will fall back to total)
 */
export function usableMeasureOrderFilters(
  field: ExploreField,
  crossDirectionFields: ExploreField[],
): ExploreMeasureOrderFilter[] {
  const filters = field.sort?.measureOrder?.filter ?? [];
  const result: ExploreMeasureOrderFilter[] = [];

  // Create a map of dimensionId to filter for quick lookup
  const filterMap = new Map<string, ExploreMeasureOrderFilter>();
  for (const filter of filters) {
    filterMap.set(filter.dimensionId, filter);
  }

  // Iterate over crossDirectionFields and collect matching filters
  // Stop when we encounter a field that doesn't have a matching filter
  for (const crossField of crossDirectionFields) {
    const filter = filterMap.get(crossField.id);
    if (filter) {
      result.push(filter);
    } else {
      // Stop at the first crossDirectionField that doesn't have a matching filter
      break;
    }
  }

  return result;
}

/*
 * Returns the measure field that the sort order is pointing to
 * it's possible for a sort order to point to a measure field that is not in the pivot
 * in this case we will return undefined
 */

export function measureFromOrder(
  field: ExploreField,
  measureFields: ExploreField[],
): ExploreField | undefined {
  if (
    field.sort?.mode !== "measure-ascending" &&
    field.sort?.mode !== "measure-descending"
  ) {
    return undefined;
  }
  return measureFields.find(
    (f) => f.id === field.sort?.measureOrder?.measureId,
  );
}

/* it's possible for a sort order to point to a measure field that is not in the pivot
 * in this case we will return false
 */
export function hasValidMeasureOrder(
  field: ExploreField,
  measureFields: ExploreField[],
): boolean {
  return measureFromOrder(field, measureFields) != null;
}
